import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Chat } from "../../models";
import { createChat, getChat, updateChat } from "../../api";
import Header from "../../header/header";
import { Button, LinearProgress, Pagination, TextField } from "@mui/material";
import { ChatMessageCard } from "../components/ChatMessageCard";

type ChatMessage = { id?: number, shownAtSeconds: number, username: string, content: string };

const itemsPerPage = 10;

export const ChatDetailPage: React.FC = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Data state
  const [chat, setChat] = useState<Omit<Chat, "id" | "createdAt" | "updatedAt"> | null>(null);
  const [error, setError] = useState<any | null>(null);

  /// Search state
  const [page, setPage] = useState(1);

  // Form
  const [chatErrors, setChatErrors] = useState<Record<keyof Chat, string>>();
  const [dirty, setDirty] = useState(false);

  const loading = useMemo(() => !chat && !error, [chat, error]);

  const [submitting, setSubmitting] = useState(false);

  const paginatedMessages = useMemo(() => chat?.messages?.slice((page - 1) * itemsPerPage, page * itemsPerPage) ?? [], [chat, page]);

  useEffect(() => {
    setPage(1);
  }, [error, loading]);

  const fetchData = useCallback(async () => {
    setError(null);

    try {
      if (id === "new") {
        setChat({
          name: "",
          status: "active",
          messages: [],
        });
      } else {
        const chat = await getChat(Number(id));
        setChat(chat);
      }
    } catch (error) {
      setError(error);
    }
  }, [id]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function validate() {
    const errors: Record<string, string> = {};

    if (!chat?.name) {
      errors.name = "Name is required";
    }

    if (Object.keys(errors).length > 0) {
      setChatErrors(errors as Record<keyof Chat, string>);
    } else {
      setChatErrors(undefined);
    }

    return Object.keys(errors).length === 0;
  }

  function handleChatChange(chat: Omit<Chat, "id" | "createdAt" | "updatedAt">) {
    if (dirty) {
      validate();
    }

    setChat(chat);
  }

  function handleMessageChange(index: number, message: ChatMessage) {
    if (!chat) return;
    setChat({
      ...chat,
      messages: chat.messages.map((m, i) => i === index ? message : m),
    });
  }

  async function handleChatSave() {
    if (submitting) {
      return;
    }

    if (!validate()) {
      setDirty(true);
      return;
    }

    setDirty(false);
    enqueueSnackbar("Saving...", { variant: "info" });
    setSubmitting(true);

    try {
      if (id === "new") {
        const result = await createChat(chat!);

        enqueueSnackbar("Chat created", { variant: "success" });
        navigate(`/content/chats/${result.id}`);
      } else {
        await updateChat(Number(id), chat!);

        enqueueSnackbar("Chat saved", { variant: "success" });
        await fetchData();
      }
    } catch (e) {
      enqueueSnackbar("Failed to save chat, ensure are messages are filled", { variant: "error" });
      console.error(e);
    }

    setSubmitting(false);
  }

  return (
    <>
      <Header />
      {loading && <div style={{ width: "50%" }}>
        <LinearProgress />
      </div>}
      {error && <div>Error: {error}</div>}
      {!loading && !error && <div className="social" style={{ minHeight: "100vh", paddingTop: "0" }}>
        <div className="settings">
          <TextField
            error={!!chatErrors?.name}
            helperText={chatErrors?.name}
            className="spacer"
            variant="outlined"
            label="Chat Name"
            name="name"
            value={chat?.name ?? ''}
            onChange={(e) => {
              if (!chat) return;
              const field = e.target.name;
              handleChatChange({ ...chat, [field]: e.target.value });
            }}
          />
        </div>
        <div className="box" style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "4rem", paddingBottom: "4rem" }}>
          {paginatedMessages.map((message, index) => {
            const nonPaginatedIndex = (page - 1) * itemsPerPage + index;

            return (
              <div style={{ width: "80rem" }} key={nonPaginatedIndex}>
                <ChatMessageCard
                  message={message}
                  onMessageChange={(message) => handleMessageChange(nonPaginatedIndex, message)}
                  onMessageDelete={() => {
                    setChat({ ...chat!, messages: chat!.messages.filter((_, i) => i !== nonPaginatedIndex) });
                  }}
                />
              </div>
            );
          })}
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setChat({ ...chat!, messages: [...chat!.messages, { shownAtSeconds: 0, username: "", content: "" }] });

              setPage(Math.ceil(((chat?.messages?.length ?? 0) + 1) / itemsPerPage));
            }}
          >
            Add Message
          </Button>
        </div>
        <div className="links">
          <Link to="/content/chats">
            <Button
              disabled={submitting}
              variant="contained"
              color="primary"
              className="spacer"
            >
              Back
            </Button>
          </Link>
          <Button
            disabled={submitting}
            variant="contained"
            color="primary"
            onClick={handleChatSave}
          >
            Save
          </Button>
        </div>
        {!loading && !error && <div className="pagination-container" style={{ position: "fixed" }}>
          <Pagination color="secondary" count={Math.ceil((chat?.messages?.length ?? 0) / itemsPerPage)} page={page} onChange={(_, page) => setPage(page)} />
        </div>}
      </div>}
    </>
  );
};
